import { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../lib/analytics';
import type { CheckoutLink } from '../../lib/checkoutClient';
import { rememberOpenedCheckout } from '../../lib/anonymousCheckoutClaim';

type PendingCheckout = { url: string; kind?: CheckoutLink['kind']; params: Record<string, string | number | boolean> };

export function useCheckoutNavigation() {
  const [pending, setPending] = useState<PendingCheckout | null>(null);
  const clear = useCallback(() => setPending(null), []);
  const navigate = useCallback((link: CheckoutLink, params: PendingCheckout['params']) => {
    if (link.kind !== 'billing_management') rememberOpenedCheckout(link.url);
    if (link.preserveInNewTab) {
      setPending({ url: link.url, kind: link.kind, params });
    } else {
      trackEvent(link.kind === 'billing_management' ? 'billing_management_opened' : 'checkout_redirected', params);
      window.location.href = link.url;
    }
  }, []);
  return { pending, navigate, clear };
}

export default function CheckoutContinuation({ pending, lang }: { pending: PendingCheckout | null; lang: string }) {
  const link = useRef<HTMLAnchorElement>(null);
  useEffect(() => { if (pending) link.current?.focus(); }, [pending]);
  if (!pending) return null;
  const billing = pending.kind === 'billing_management';
  const copy = billing
    ? lang === 'ja'
      ? ['音声と編集内容を残すため、このタブを開いたまま別タブで請求ページを確認してください。', '請求ページを別タブで開く']
      : lang === 'zh'
      ? ['请保持此标签页打开，并在新标签页中查看账单，以保留音频和编辑内容。', '在新标签页中打开账单']
      : ['Keep this tab open to preserve your audio and edits, and review billing in a new tab.', 'Open billing in a new tab']
    : lang === 'ja'
    ? ['音声と編集内容を残すため、このタブを開いたまま別タブで決済してください。', '決済を別タブで開く']
    : lang === 'zh'
    ? ['请保持此标签页打开，并在新标签页中结账，以保留音频和编辑内容。', '在新标签页中结账']
    : ['Keep this tab open to preserve your audio and edits, and continue checkout in a new tab.', 'Open checkout in a new tab'];
  return <div role="status" className="checkout-continuation" style={{ padding: 14, marginBottom: 16, border: '1px solid #888', borderRadius: 8 }}>
    <p>{copy[0]}</p>
    <a ref={link} href={pending.url} target="_blank" rel="noopener noreferrer"
      onClick={() => trackEvent(billing ? 'billing_management_opened' : 'checkout_redirected', { ...pending.params, new_tab: true })}
      style={{ display: 'inline-block', padding: '12px 16px', background: '#74530c', color: '#fff', borderRadius: 6 }}>
      {copy[1]}
    </a>
  </div>;
}
